import React, { useState, useEffect } from "react";
import supabase from "../../lib/supabase";

export default function Projects() {
  const [currentProjects, setCurrentProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [tagInput, setTagInput] = useState("");
  // form state
  const [form, setForm] = useState({
    title: "",
    type: "",
    link: "",
    description: "",
    techTags: "",
  });
  const [files, setFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]); // [{url, contentType}]
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    getProjects();
  }, []);

  const getProjects = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("projects")
        .select("id,title,type,description,link,media,created_at,tech_tags")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setCurrentProjects(data || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      link: "",
      type: "",
      techTags: [],
    });
    setFiles([]);
    setExistingMedia([]);
    setSuccess("");
    setError("");
    setIsEditing(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess("");
    setError("");
    try {
      // Upload new media files to Supabase Storage (bucket: projects)
      const newMedia = [];
      for (const file of files) {
        const filePath = `projects/${Date.now()}_${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from("projects")
          .upload(filePath, file);
        if (uploadError) throw uploadError;
        const { data: publicData } = supabase.storage
          .from("projects")
          .getPublicUrl(filePath);
        newMedia.push({ url: publicData.publicUrl, contentType: file.type });
      }

      const media = [...existingMedia, ...newMedia];
      const { title, type, description, link, techTags } = form;

      if (isEditing && editingId) {
        const { data: updated, error: updateError } = await supabase
          .from("projects")
          .update({
            title,
            type,
            description,
            link,
            media,
            tech_tags: techTags,
          })
          .eq("id", editingId)
          .select("id,title,type,description,link,media,created_at")
          .single();
        if (updateError) throw updateError;
        setCurrentProjects((prev) =>
          prev.map((p) => (p.id === editingId ? updated : p))
        );
        setSuccess("Project updated successfully");
      } else {
        const { data: inserted, error: insertError } = await supabase
          .from("projects")
          .insert({
            title,
            type,
            description,
            link,
            media,
            tech_tags: techTags,
          })
          .select("id,title,type,description,link,media,created_at")
          .single();
        if (insertError) throw insertError;
        setSuccess("Project saved successfully");
        setCurrentProjects((prev) => [inserted, ...prev]);
      }

      resetForm();
      setModalOpen(false);
    } catch (e) {
      setError(e.message || "Failed to save project");
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const onClickNew = () => {
    resetForm();
    setModalOpen(true);
  };

  const onClickEdit = (project) => {
    setIsEditing(true);
    setEditingId(project.id);
    // setForm(()=>{})
    setForm({
      title: project.title || "",
      type: project.type || "",
      link: project.link || "",
      description: project.description || "",
      techTags: project.tech_tags || [],
    });
    setExistingMedia(Array.isArray(project.media) ? project.media : []);
    setFiles([]);
    setModalOpen(true);
  };

  const onClickDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const { error: delError } = await supabase
        .from("projects")
        .delete()
        .eq("id", deleteId);
      if (delError) throw delError;
      setCurrentProjects((prev) => prev.filter((p) => p.id !== deleteId));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setDeleteId(null);
      setConfirmOpen(false);
    }
  };

  const removeExistingMedia = (index) => {
    setExistingMedia((prev) => prev.filter((_, i) => i !== index));
  };
  const handleAddTag = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      if (!form.techTags.includes(tagInput.trim())) {
        setForm((prev) => ({
          ...prev,
          techTags: [...prev.techTags, tagInput.trim()], // ✅ Use prev.techTags
        }));
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setForm((prev) => ({
      ...prev,
      techTags: form.techTags.filter((t) => t !== tag),
    }));
    // setTechTags(techTags.filter((t) => t !== tag));
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  return (
    <div className="overflow-x-auto flex justify-center w-full">
      <div className="w-full max-w-6xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg">Projects</h3>
          <button
            className="px-3 py-2 bg-blue-600 rounded"
            onClick={onClickNew}
          >
            New Project
          </button>
        </div>
        <table className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg shadow-lg">
          <thead className="bg-gray-800">
            <tr className="text-left">
              <th className="py-4 px-6 border-b border-gray-700 w-12">#</th>
              <th className="py-4 px-6 border-b border-gray-700 w-64">Title</th>
              <th className="py-4 px-6 border-b border-gray-700 w-48">Type</th>
              <th className="py-4 px-6 border-b border-gray-700 w-auto">
                Description
              </th>
              <th className="py-4 px-6 border-b border-gray-700 w-48">Link</th>
              <th className="py-4 px-6 border-b border-gray-700 w-40">
                Created
              </th>
              <th className="py-4 px-6 border-b border-gray-700 w-32 text-center">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : currentProjects.length > 0 ? (
              currentProjects.map((project, index) => (
                <tr key={project.id} className="hover:bg-gray-800">
                  <td className="py-3 px-6 border-b border-gray-700">
                    {index + 1}
                  </td>
                  <td className="py-3 px-6 border-b border-gray-700">
                    {project.title}
                  </td>
                  <td className="py-3 px-6 border-b border-gray-700">
                    {project.type}
                  </td>
                  <td className="py-3 px-6 border-b border-gray-700 truncate max-w-[360px]">
                    {project.description}
                  </td>
                  <td className="py-3 px-6 border-b border-gray-700">
                    {project.link ? (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 underline truncate block max-w-[200px]"
                      >
                        {project.link}
                      </a>
                    ) : (
                      <span className="text-gray-400">No link</span>
                    )}
                  </td>
                  <td className="py-3 px-6 border-b border-gray-700">
                    {project.created_at
                      ? new Date(project.created_at).toLocaleString()
                      : "-"}
                  </td>
                  <td className="py-3 px-6 border-b border-gray-700 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        className="px-2 py-1 bg-gray-700 rounded"
                        onClick={() => onClickEdit(project)}
                      >
                        Edit
                      </button>
                      <button
                        className="px-2 py-1 bg-red-600 rounded"
                        onClick={() => onClickDelete(project.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-400">
                  No projects found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          {/* Background overlay */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setModalOpen(false)}
          />

          {/* Modal content */}
          <div className=" relative z-10  w-full max-w-xl  bg-gray-900 text-white  p-6 rounded-lg border border-gray-700  my-10 max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-gray-900 z-20 pb-2">
              <h4 className="text-lg">
                {isEditing ? "Edit Project" : "New Project"}
              </h4>
              <button
                className="bg-gray-700 px-2 py-1 rounded"
                onClick={() => setModalOpen(false)}
              >
                Close
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block mb-1">Title</label>
                <input
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded"
                  value={form.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block mb-1">Type / Category</label>
                <input
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded"
                  placeholder="mobile app, ai agent, web app, IOT"
                  value={form.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                  required
                />
              </div>

              {/* ✅ Tech Tags */}
              <div className="mb-4">
                <label className="block mb-1">Tech Used</label>
                <div className="w-full mb-3 rounded bg-[#1f2937] flex flex-wrap gap-2 p-2 min-h-[44px]">
                  {form.techTags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-blue-600 text-white px-2 py-1 rounded-full text-sm flex items-center gap-1"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 text-xs hover:text-red-300"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  <input
                    className="bg-transparent outline-none flex-1 text-white"
                    placeholder="Press Enter to add tag..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-1">Link</label>
                <input
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded"
                  placeholder="https://app_link"
                  value={form.link}
                  onChange={(e) => handleChange("link", e.target.value)}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block mb-1">Description</label>
                <textarea
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded"
                  rows={4}
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  required
                />
              </div>

              {existingMedia?.length > 0 && (
                <div className="mb-4">
                  <label className="block mb-2">Existing Media</label>
                  <div className="grid grid-cols-2 gap-3">
                    {existingMedia.map((m, idx) => (
                      <div
                        key={idx}
                        className="relative border border-gray-700 rounded p-2"
                      >
                        {m.contentType?.startsWith("image/") ? (
                          <img
                            src={m.url}
                            alt="media"
                            className="w-full h-32 object-cover rounded"
                          />
                        ) : (
                          <video
                            src={m.url}
                            className="w-full h-32 rounded"
                            controls
                          />
                        )}
                        <button
                          type="button"
                          className="absolute top-2 right-2 bg-red-600 px-2 py-1 text-xs rounded"
                          onClick={() => removeExistingMedia(idx)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block mb-1">Add Media (images/videos)</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                />
              </div>

              {success && <p className="text-green-400 mb-2">{success}</p>}
              {error && <p className="text-red-400 mb-2">{error}</p>}

              <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-gray-900 pt-3">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-700 rounded"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 rounded disabled:opacity-50"
                >
                  {submitting
                    ? isEditing
                      ? "Updating..."
                      : "Saving..."
                    : isEditing
                    ? "Update Project"
                    : "Save Project"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setConfirmOpen(false)}
          />
          <div className="relative z-10 w-full max-w-md bg-gray-900 text-white p-6 rounded-lg border border-gray-700">
            <h4 className="text-lg mb-2">Delete Project</h4>
            <p className="mb-4">
              Are you sure you want to delete this project? This action cannot
              be undone.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-700 rounded"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 rounded"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
